/**
 * StatusBanner — semáforo "Estado General" del Centro de Control.
 * Combinado, peor regla manda. Muestra el color global + las señales que lo
 * componen (NS, fallos, salud IA, riesgo), para que sea transparente POR QUÉ
 * está en ese color y no contradiga los paneles de abajo.
 */
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, AlertOctagon } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Skeleton } from '../../../components/ui/Skeleton';

const LEVEL = {
  success: {
    icon: CheckCircle2,
    word: 'Operación normal',
    ring: 'border-status-success/40 bg-status-success-soft',
    accent: 'text-status-success',
    bar: 'bg-status-success',
  },
  warning: {
    icon: AlertTriangle,
    word: 'Requiere atención',
    ring: 'border-status-warning/40 bg-status-warning-soft',
    accent: 'text-status-warning',
    bar: 'bg-status-warning',
  },
  error: {
    icon: AlertOctagon,
    word: 'Estado crítico',
    ring: 'border-status-error/40 bg-status-error-soft',
    accent: 'text-status-error',
    bar: 'bg-status-error',
  },
};

const DOT = {
  success: 'bg-status-success',
  warning: 'bg-status-warning',
  error: 'bg-status-error',
};

export default function StatusBanner({ status, loading }) {
  const { t } = useTranslation();

  if (loading) {
    return <Skeleton className="h-24 rounded-2xl" />;
  }

  const level = status?.level || 'success';
  const cfg = LEVEL[level];
  const Icon = cfg.icon;
  const signals = status?.signals || [];

  return (
    <div
      className={cn(
        'relative flex flex-col gap-3 overflow-hidden rounded-2xl border p-4 md:flex-row md:items-center md:gap-5 md:p-5',
        cfg.ring,
      )}
      role="status"
      aria-live="polite"
    >
      <span className={cn('absolute inset-y-0 left-0 w-1.5', cfg.bar)} aria-hidden />

      <div className="flex items-center gap-3 md:gap-4 md:pl-2">
        <span className={cn('grid h-12 w-12 place-items-center rounded-xl bg-bg/70', cfg.accent)}>
          <Icon className="h-7 w-7" strokeWidth={2.1} />
        </span>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
            {t('Estado General')}
          </div>
          <div className={cn('text-xl font-bold leading-tight md:text-2xl', cfg.accent)}>
            {t(cfg.word)}
          </div>
        </div>
      </div>

      {/* Señales que componen el semáforo */}
      <div className="flex flex-1 flex-wrap items-center gap-2 md:justify-end">
        {signals.map((s) => (
          <span
            key={s.key}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-bg/80 px-3 py-1.5"
          >
            <span className={cn('h-2.5 w-2.5 rounded-full', DOT[s.level])} aria-hidden />
            <span className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
              {t(s.label)}
            </span>
            <span className="text-sm font-bold tabular-nums text-fg">{s.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
